import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../api/axios';
import ProductCard from '../../components/user/ProductCard';

function Icon({ name, className = 'h-4 w-4' }) {
  const paths = {
    search: <><circle cx="11" cy="11" r="7"/><path d="m20 20-4-4"/></>,
    filter: <><path d="M4 6h16M7 12h10M10 18h4"/></>,
    close: <path d="m6 6 12 12M18 6 6 18"/>,
    left: <path d="m15 18-6-6 6-6"/>,
    right: <path d="m9 18 6-6-6-6"/>,
  };
  return <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name]}</svg>;
}

const priceOptions = [
  { value: '', label: 'Any price' },
  { value: 'under_300', label: 'Under ₹300', min: 0, max: 299 },
  { value: '300_600', label: '₹300 to ₹600', min: 300, max: 600 },
  { value: 'above_600', label: 'Above ₹600', min: 601 },
];

const typeOptions = [
  { value: '', label: 'All products' },
  { value: 'simple', label: 'Single items' },
  { value: 'variable', label: 'Products with options' },
  { value: 'grouped', label: 'Gift sets and bundles' },
];

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');

  const search = searchParams.get('search') || '';
  const category = searchParams.get('category') || '';
  const productType = searchParams.get('productType') || '';
  const price = searchParams.get('price') || '';
  const sort = searchParams.get('sort') || '';
  const page = Number(searchParams.get('page') || 1);
  const selectedPrice = priceOptions.find((option) => option.value === price) || priceOptions[0];
  const activeFilterCount = [productType, price].filter(Boolean).length;

  useEffect(() => {
    api.get('/categories').then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setSearchQuery(search);
  }, [search]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const params = { page, limit: 12 };
    if (search) params.search = search;
    if (category) params.category = category;
    if (productType) params.productType = productType;
    if (sort) params.sort = sort;
    if (selectedPrice.min !== undefined) params.minPrice = selectedPrice.min;
    if (selectedPrice.max !== undefined) params.maxPrice = selectedPrice.max;
    api.get('/products', { params }).then(({ data }) => {
      if (!active) return;
      setItems(data.items || []);
      setPages(data.pages || 1);
      setTotal(data.total || 0);
    }).catch(() => {
      if (active) { setItems([]); setTotal(0); }
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [search, category, productType, sort, page, price, selectedPrice.min, selectedPrice.max]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    if (key !== 'page') next.set('page', '1');
    setSearchParams(next);
  };

  const clearFilters = () => {
    const next = new URLSearchParams();
    if (search) next.set('search', search);
    if (category) next.set('category', category);
    setSearchParams(next);
    setFiltersOpen(false);
  };

  const clearAll = () => {
    setSearchParams({});
    setSearchQuery('');
    setFiltersOpen(false);
  };

  const submitSearch = (event) => {
    event.preventDefault();
    updateParam('search', searchQuery.trim());
  };

  const selectedCategory = categories.find((item) => (item.slug || item.name) === category);
  const heading = search ? `Results for “${search}”` : selectedCategory?.name || 'Shop sandalwood';

  const FilterPanel = ({ mobile = false }) => (
    <div className={mobile ? 'space-y-7' : 'space-y-8'}>
      <fieldset>
        <legend className="mb-3 text-[11px] font-bold uppercase text-[#241914]">Product format</legend>
        <div className="space-y-2.5">
          {typeOptions.map((option) => <label key={option.value} className="flex cursor-pointer items-center gap-3 text-xs text-[#5E514A]"><input type="radio" name={mobile ? 'mobile-product-type' : 'desktop-product-type'} checked={productType === option.value} onChange={() => updateParam('productType', option.value)} className="h-4 w-4 accent-[#9D772D]" /><span>{option.label}</span></label>)}
        </div>
      </fieldset>
      <fieldset>
        <legend className="mb-3 text-[11px] font-bold uppercase text-[#241914]">Price</legend>
        <div className="space-y-2.5">
          {priceOptions.map((option) => <label key={option.value} className="flex cursor-pointer items-center gap-3 text-xs text-[#5E514A]"><input type="radio" name={mobile ? 'mobile-price' : 'desktop-price'} checked={price === option.value} onChange={() => updateParam('price', option.value)} className="h-4 w-4 accent-[#9D772D]" /><span>{option.label}</span></label>)}
        </div>
      </fieldset>
      {activeFilterCount > 0 && <button onClick={clearFilters} className="text-xs font-bold text-[#9D772D] underline underline-offset-4">Clear product filters</button>}
      {mobile && <button onClick={() => setFiltersOpen(false)} className="h-11 w-full rounded bg-[#241914] text-xs font-bold uppercase text-white">Show {total} product{total === 1 ? '' : 's'}</button>}
    </div>
  );

  return (
    <div className="min-h-screen bg-white text-[#241914]">
      <header className="border-b border-[#E8E1DB] bg-[#F7F5F2]">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 sm:py-6">
          <div className="relative h-[230px] overflow-hidden rounded-md bg-[#EEE8E1] sm:h-[260px] lg:h-[280px]">
            <img src="/assets/shop-sandalwood-collection-banner.webp" alt="A curated collection of carved Mysuru sandalwood products" className="absolute inset-0 h-full w-full object-cover object-[64%_center] sm:object-center" />
            <div className="absolute inset-y-0 left-0 flex w-[58%] flex-col justify-center px-5 sm:w-1/2 sm:px-9 lg:px-12">
              <p className="text-[9px] font-bold uppercase text-[#87631F] sm:text-[10px]">The Chandana Aura collection</p>
              <h1 className="mt-2 font-heading text-[29px] font-semibold leading-[1.04] text-[#241914] sm:text-[40px] lg:text-[46px]">{heading}</h1>
              <p className="mt-3 hidden max-w-md text-xs leading-5 text-[#5E514A] sm:block">Authentic Mysuru sandalwood pieces for ritual, everyday use, and thoughtful gifting.</p>
              <a href="#shop-products" className="mt-4 w-fit border-b border-[#87631F] pb-1 text-[9px] font-bold uppercase text-[#87631F] sm:text-[10px]">Explore the collection</a>
            </div>
          </div>
        </div>
      </header>

      <div className="border-b border-[#E8E1DB] bg-white">
        <div className="mx-auto flex max-w-7xl flex-col gap-1 px-4 pt-3 sm:px-6 md:flex-row md:items-center md:gap-7 md:pt-0">
          <form onSubmit={submitSearch} className="flex h-10 w-full shrink-0 overflow-hidden rounded border border-[#CFC5BD] bg-white md:w-[300px]">
            <span className="grid w-10 shrink-0 place-items-center text-[#76675E]"><Icon name="search" /></span>
            <input value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} placeholder="Search the collection" className="min-w-0 flex-1 bg-transparent pr-2 text-[11px] outline-none" aria-label="Search products" />
            <button className="border-l border-[#CFC5BD] px-3 text-[10px] font-bold uppercase text-[#9D772D]">Search</button>
          </form>
          <div className="flex min-w-0 flex-1 items-center gap-6 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <button onClick={() => updateParam('category', '')} className={`shrink-0 border-b-2 py-3.5 text-[11px] font-bold ${!category ? 'border-[#9D772D] text-[#241914]' : 'border-transparent text-[#76675E] hover:text-[#241914]'}`}>All</button>
            {categories.map((item) => { const value = item.slug || item.name; return <button key={item._id} onClick={() => updateParam('category', value)} className={`shrink-0 border-b-2 py-3.5 text-[11px] font-bold ${category === value ? 'border-[#9D772D] text-[#241914]' : 'border-transparent text-[#76675E] hover:text-[#241914]'}`}>{item.name}</button>; })}
          </div>
        </div>
      </div>

      <main id="shop-products" className="mx-auto max-w-7xl scroll-mt-24 px-4 py-3 sm:px-6 sm:py-4">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#E8E1DB] pb-3">
          <div className="flex flex-wrap items-center gap-3">
            <button onClick={() => setFiltersOpen(true)} className="flex h-10 items-center gap-2 rounded border border-[#CFC5BD] px-3 text-[11px] font-bold uppercase lg:hidden">
              <Icon name="filter" />Filters{activeFilterCount > 0 && <span className="grid h-5 w-5 place-items-center rounded-full bg-[#241914] text-[9px] text-white">{activeFilterCount}</span>}
            </button>

            {/* Products count */}
            <p className="text-[11px] text-[#76675E] whitespace-nowrap">
              <span className="font-bold text-[#241914]">{total}</span> product{total === 1 ? '' : 's'}
            </p>

            {/* Added filters section moved to right side of products count */}
            {(search || productType || price) && (
              <div className="flex flex-wrap items-center gap-1.5 pl-2 sm:pl-3 border-l border-[#E8E1DB] text-[10px]">
                {search && (
                  <button
                    type="button"
                    onClick={() => { updateParam('search', ''); setSearchQuery(''); }}
                    className="inline-flex items-center gap-1 rounded border border-[#D9D0C8] bg-white px-2.5 py-1 text-[#241914] hover:border-[#9D772D] transition-colors"
                  >
                    <span>Search: {search}</span>
                    <span className="text-[#9D772D] font-bold">×</span>
                  </button>
                )}
                {productType && (
                  <button
                    type="button"
                    onClick={() => updateParam('productType', '')}
                    className="inline-flex items-center gap-1 rounded border border-[#D9D0C8] bg-white px-2.5 py-1 text-[#241914] hover:border-[#9D772D] transition-colors"
                  >
                    <span>{typeOptions.find((item) => item.value === productType)?.label}</span>
                    <span className="text-[#9D772D] font-bold">×</span>
                  </button>
                )}
                {price && (
                  <button
                    type="button"
                    onClick={() => updateParam('price', '')}
                    className="inline-flex items-center gap-1 rounded border border-[#D9D0C8] bg-white px-2.5 py-1 text-[#241914] hover:border-[#9D772D] transition-colors"
                  >
                    <span>{selectedPrice.label}</span>
                    <span className="text-[#9D772D] font-bold">×</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={clearAll}
                  className="px-2 py-1 font-bold text-[#9D772D] hover:underline whitespace-nowrap"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>

          <label className="flex items-center gap-2 text-[11px] text-[#76675E] shrink-0">
            <span className="hidden sm:inline">Sort</span>
            <select
              value={sort}
              onChange={(event) => updateParam('sort', event.target.value)}
              className="h-10 rounded border border-[#CFC5BD] bg-white px-2 text-[11px] font-semibold text-[#241914] outline-none"
            >
              <option value="">Newest</option>
              <option value="price_asc">Price: low to high</option>
              <option value="price_desc">Price: high to low</option>
              <option value="rating">Customer rating</option>
            </select>
          </label>
        </div>

        <div className="mt-4 grid gap-7 lg:grid-cols-[190px_minmax(0,1fr)] lg:gap-9">
          <aside className="hidden border-r border-[#E8E1DB] pr-7 lg:block"><div className="sticky top-24"><FilterPanel /></div></aside>
          <section aria-live="polite">
            {loading ? <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-5 xl:grid-cols-4">{Array.from({ length: 8 }).map((_, index) => <div key={index} className="animate-pulse"><div className="aspect-square rounded bg-[#EEEAE6]"/><div className="mt-3 h-3 w-4/5 rounded bg-[#EEEAE6]"/><div className="mt-2 h-3 w-2/5 rounded bg-[#EEEAE6]"/></div>)}</div> : items.length === 0 ? <div className="grid min-h-[320px] place-items-center border-y border-[#E8E1DB] text-center"><div><h2 className="font-heading text-2xl font-semibold">No matching products</h2><p className="mt-2 text-xs text-[#76675E]">Try another category or remove some filters.</p><button onClick={clearAll} className="mt-5 rounded bg-[#241914] px-5 py-2.5 text-xs font-bold uppercase text-white">View all products</button></div></div> : <div className="grid grid-cols-2 gap-x-3 gap-y-6 sm:grid-cols-3 sm:gap-x-5 sm:gap-y-8 xl:grid-cols-4">{items.map((item) => <ProductCard key={item._id} product={item} />)}</div>}

            {pages > 1 && <nav className="mt-10 flex items-center justify-center gap-1" aria-label="Product pages"><button disabled={page === 1} onClick={() => updateParam('page', String(page - 1))} className="grid h-9 w-9 place-items-center rounded border border-[#D9D0C8] disabled:opacity-30" aria-label="Previous page"><Icon name="left" /></button>{Array.from({ length: pages }).map((_, index) => { const pageNumber = index + 1; return <button key={pageNumber} onClick={() => updateParam('page', String(pageNumber))} className={`h-9 min-w-9 rounded px-2 text-xs font-bold ${page === pageNumber ? 'bg-[#241914] text-white' : 'border border-[#D9D0C8] text-[#5E514A]'}`}>{pageNumber}</button>; })}<button disabled={page === pages} onClick={() => updateParam('page', String(page + 1))} className="grid h-9 w-9 place-items-center rounded border border-[#D9D0C8] disabled:opacity-30" aria-label="Next page"><Icon name="right" /></button></nav>}
          </section>
        </div>
      </main>

      {filtersOpen && <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="Product filters"><button className="absolute inset-0 bg-black/35" onClick={() => setFiltersOpen(false)} aria-label="Close filters"/><div className="absolute inset-y-0 left-0 w-[min(86vw,340px)] overflow-y-auto bg-white p-6 shadow-xl"><div className="mb-7 flex items-center justify-between border-b border-[#E8E1DB] pb-4"><div><p className="font-heading text-2xl font-semibold">Filters</p><p className="text-[10px] text-[#76675E]">Refine the collection</p></div><button onClick={() => setFiltersOpen(false)} className="grid h-9 w-9 place-items-center rounded-full border border-[#D9D0C8]" aria-label="Close filters"><Icon name="close" /></button></div><FilterPanel mobile /></div></div>}
    </div>
  );
}
